'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function DepreciationCalculator() {
  const t = useTranslations("tools.depreciationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const [inputs, setInputs] = useState({
    method: 'straight',
    cost: 11000,
    salvage: 1000,
    years: 5,
    factor: 2,
    round: true,
    partial: false,
    convention: 'halfyear',
    startDate: new Date().toISOString().split('T')[0]
  });

  const [results, setResults] = useState<any>(null);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number, round: boolean) => {
    if (round) {
      value = Math.round(value);
    }
    return '$' + value.toLocaleString('en-US', {
      minimumFractionDigits: round ? 0 : 2,
      maximumFractionDigits: round ? 0 : 2
    });
  };

  const calculateStraightLine = (cost: number, salvage: number, years: number) => {
    const depreciableAmount = cost - salvage;
    const annualDepreciation = depreciableAmount / years;
    const schedule = [];
    let accumulated = 0;

    for (let year = 1; year <= years; year++) {
      const beginningValue = cost - accumulated;
      const depreciation = annualDepreciation;
      accumulated += depreciation;
      const endingValue = cost - accumulated;

      schedule.push({
        year,
        beginningValue,
        depreciation,
        accumulated,
        endingValue
      });
    }

    return schedule;
  };

  const calculateDecliningBalance = (cost: number, salvage: number, years: number, factor: number) => {
    const rate = factor / years;
    const schedule = [];
    let bookValue = cost;
    let accumulated = 0;

    for (let year = 1; year <= years; year++) {
      const beginningValue = bookValue;
      let depreciation = bookValue * rate;

      if (bookValue - depreciation < salvage) {
        depreciation = bookValue - salvage;
      }

      accumulated += depreciation;
      bookValue -= depreciation;

      schedule.push({
        year,
        beginningValue,
        depreciation,
        accumulated,
        endingValue: bookValue
      });

      if (bookValue <= salvage) {
        break;
      }
    }

    return schedule;
  };

  const calculateSumOfYears = (cost: number, salvage: number, years: number) => {
    const depreciableAmount = cost - salvage;
    const sumOfYears = (years * (years + 1)) / 2;
    const schedule = [];
    let accumulated = 0;

    for (let year = 1; year <= years; year++) {
      const beginningValue = cost - accumulated;
      const remainingLife = years - year + 1;
      const depreciation = (depreciableAmount * remainingLife) / sumOfYears;
      accumulated += depreciation;
      const endingValue = cost - accumulated;

      schedule.push({
        year,
        beginningValue,
        depreciation,
        accumulated,
        endingValue
      });
    }

    return schedule;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    const { cost, salvage, years, method, factor, round } = inputs;

    if (salvage >= cost) {
      alert(t('salvageError'));
      return;
    }

    let schedule;

    if (method === 'straight') {
      schedule = calculateStraightLine(cost, salvage, years);
    } else if (method === 'declining') {
      schedule = calculateDecliningBalance(cost, salvage, years, factor);
    } else if (method === 'sum') {
      schedule = calculateSumOfYears(cost, salvage, years);
    }

    const totalDepreciation = cost - salvage;
    const annualAverage = totalDepreciation / years;
    const finalValue = salvage;

    setResults({
      schedule,
      totalDepreciation,
      annualAverage,
      finalValue
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, marginTop: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const selectStyle = { ...inputStyle };

  const formRowStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const radioGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1.5rem', marginTop: '0.5rem', flexWrap: 'wrap' as const },
    {},
    {},
    {}
  );

  const radioLabelStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontWeight: 500,
    color: '#475569'
  };

  const partialYearOptionsStyle = {
    background: '#f8fafc',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: inputs.partial ? 'block' : 'none'
  };

  const calculateBtnStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      fontWeight: 600,
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      width: '100%',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.15rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b',
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', fontWeight: 600 },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const summaryGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const summaryCardStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.5rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' },
    { padding: '1rem' },
    { padding: '1.15rem' },
    { padding: '1.25rem' }
  );

  const summaryLabelStyle = {
    fontSize: '0.85rem',
    color: '#64748b',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    marginBottom: '0.5rem'
  };

  const summaryValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const scheduleTableStyle = {
    width: '100%',
    background: 'white',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const
  };

  const thStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'left' as const,
      fontWeight: 600
    },
    { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
    { padding: '0.875rem 0.75rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const tdStyle = getResponsiveStyle(
    { borderBottom: '1px solid #e2e8f0', color: colors.textPrimary },
    { padding: '0.75rem 0.5rem', fontSize: '0.85rem' },
    { padding: '0.875rem 0.75rem', fontSize: '0.9rem' },
    { padding: '1rem', fontSize: '0.95rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.1rem', lineHeight: 1.7 },
    { fontSize: '1.15rem', lineHeight: 1.8 }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
    marginBottom: '1rem'
  };

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = {
    ...strongStyle,
    display: 'block',
    marginBottom: '0.5rem',
    fontSize: screenSize === 'mobile' ? '1.05rem' : screenSize === 'tablet' ? '1.1rem' : '1.15rem'
  };

  const referenceStyle = {
    ...paragraphStyle,
    fontSize: screenSize === 'mobile' ? '0.9rem' : screenSize === 'tablet' ? '0.95rem' : '1rem',
    marginBottom: '0.5rem'
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>📊 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="method">{t('methodLabel')}</label>
              <select
                style={selectStyle}
                id="method"
                value={inputs.method}
                onChange={(e) => setInputs({ ...inputs, method: e.target.value })}
                required
              >
                <option value="straight">{t('methodStraight')}</option>
                <option value="declining">{t('methodDeclining')}</option>
                <option value="sum">{t('methodSum')}</option>
              </select>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="cost">{t('costLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="cost"
                  value={inputs.cost}
                  onChange={(e) => setInputs({ ...inputs, cost: parseFloat(e.target.value) })}
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="salvage">{t('salvageLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="salvage"
                  value={inputs.salvage}
                  onChange={(e) => setInputs({ ...inputs, salvage: parseFloat(e.target.value) })}
                  step="0.01"
                  min="0"
                  required
                />
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="years">{t('yearsLabel')}</label>
                <input
                  style={inputStyle}
                  type="number"
                  id="years"
                  value={inputs.years}
                  onChange={(e) => setInputs({ ...inputs, years: parseInt(e.target.value) })}
                  step="1"
                  min="1"
                  required
                />
              </div>

              {inputs.method === 'declining' && (
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="factor">{t('factorLabel')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="factor"
                    value={inputs.factor}
                    onChange={(e) => setInputs({ ...inputs, factor: parseFloat(e.target.value) })}
                    step="0.1"
                    min="0.1"
                  />
                </div>
              )}
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('roundLabel')}</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="round"
                    value="yes"
                    checked={inputs.round}
                    onChange={() => setInputs({ ...inputs, round: true })}
                    style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  {t('yes')}
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="round"
                    value="no"
                    checked={!inputs.round}
                    onChange={() => setInputs({ ...inputs, round: false })}
                    style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  {t('no')}
                </label>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>{t('partialLabel')}</label>
              <div style={radioGroupStyle}>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="partial"
                    value="yes"
                    checked={inputs.partial}
                    onChange={() => setInputs({ ...inputs, partial: true })}
                    style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  {t('yes')}
                </label>
                <label style={radioLabelStyle}>
                  <input
                    type="radio"
                    name="partial"
                    value="no"
                    checked={!inputs.partial}
                    onChange={() => setInputs({ ...inputs, partial: false })}
                    style={{ width: 'auto', marginRight: '0.5rem', cursor: 'pointer' }}
                  />
                  {t('no')}
                </label>
              </div>
            </div>

            <div style={partialYearOptionsStyle}>
              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="convention">{t('conventionLabel')}</label>
                <select
                  style={selectStyle}
                  id="convention"
                  value={inputs.convention}
                  onChange={(e) => setInputs({ ...inputs, convention: e.target.value })}
                >
                  <option value="halfyear">{t('conventionHalfYear')}</option>
                  <option value="fullmonth">{t('conventionFullMonth')}</option>
                  <option value="halfmonth">{t('conventionHalfMonth')}</option>
                </select>
              </div>

              <div style={formGroupStyle}>
                <label style={labelStyle} htmlFor="startdate">{t('startDateLabel')}</label>
                <input
                  style={inputStyle}
                  type="date"
                  id="startdate"
                  value={inputs.startDate}
                  onChange={(e) => setInputs({ ...inputs, startDate: e.target.value })}
                />
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📈 {t('resultsTitle')}</h3>

              <div style={summaryGridStyle}>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('totalDepreciation')}</div>
                  <div style={summaryValueStyle}>
                    {formatCurrency(results.totalDepreciation, inputs.round)}
                  </div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('annualAverage')}</div>
                  <div style={summaryValueStyle}>
                    {formatCurrency(results.annualAverage, inputs.round)}
                  </div>
                </div>
                <div style={summaryCardStyle}>
                  <div style={summaryLabelStyle}>{t('finalValue')}</div>
                  <div style={summaryValueStyle}>
                    {formatCurrency(results.finalValue, inputs.round)}
                  </div>
                </div>
              </div>

              <div style={scheduleTableStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>{t('tableYear')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('tableBeginning')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('tableDepreciation')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('tableAccumulated')}</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>{t('tableEnding')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.schedule.map((row: any, index: number) => (
                      <tr key={index} style={{ background: index % 2 === 1 ? '#f8fafc' : 'white' }}>
                        <td style={tdStyle}>{t('year')} {row.year}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatCurrency(row.beginningValue, inputs.round)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatCurrency(row.depreciation, inputs.round)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatCurrency(row.accumulated, inputs.round)}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          {formatCurrency(row.endingValue, inputs.round)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightStraightTitle')}</strong> {t('insightStraightDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightDecliningTitle')}</strong> {t('insightDecliningDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightSumTitle')}</strong> {t('insightSumDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insightUnitsTitle')}</strong> {t('insightUnitsDescription')}</li>
        </ul>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailDepreciableTitle')}</strong> {t('detailDepreciableDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailStraightTitle')}</strong> {t('detailStraightDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailDecliningTitle')}</strong> {t('detailDecliningDescription')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailSumTitle')}</strong> {t('detailSumDescription')}</li>
        </ul>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq1Question')}</span>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq2Question')}</span>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq3Question')}</span>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq4Question')}</span>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq5Question')}</span>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <span style={faqQuestionStyle}>{t('faq6Question')}</span>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={referenceStyle}>
          1. <a href="https://www.netsuite.com/portal/resource/articles/financial-management/depreciation.shtml" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t('reference1')}
          </a>
        </p>
        <p style={referenceStyle}>
          2. <a href="https://www.investopedia.com/terms/d/depreciation.asp" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t('reference2')}
          </a>
        </p>
        <p style={referenceStyle}>
          3. <a href="https://www.irs.gov/publications/p946" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t('reference3')}
          </a>
        </p>
        <p style={referenceStyle}>
          4. <a href="https://www.accountingtools.com/articles/what-is-depreciation.html" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            {t('reference4')}
          </a>
        </p>
      </section>
    </div>
  );
}